import _extends from "@babel/runtime/helpers/extends";
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the "Elastic License
 * 2.0", the "GNU Affero General Public License v3.0 only", and the "Server Side
 * Public License v 1"; you may not use this file except in compliance with, at
 * your election, the "Elastic License 2.0", the "GNU Affero General Public
 * License v3.0 only", or the "Server Side Public License, v 1".
 */

import React, { useState } from 'react';
import { EuiButton, EuiCodeBlock, EuiEmptyPrompt, EuiFlyout, EuiFlyoutBody, EuiFlyoutHeader, EuiFlyoutFooter, EuiLink, EuiTitle, useGeneratedHtmlId, EuiFlexGroup, EuiFlexItem, EuiButtonEmpty, EuiCopy, EuiPanel } from '@elastic/eui';
import { errorMessageStrings as strings } from './message_strings';
import { jsx as ___EmotionJSX } from "@emotion/react";
const CodePanel = props => {
  var _ref, _errorInfo$componentS, _error$stack;
  const {
    error,
    errorInfo,
    name: errorComponentName,
    onClose
  } = props;
  const simpleFlyoutTitleId = useGeneratedHtmlId({
    prefix: 'simpleFlyoutTitle'
  });
  const errorName = errorComponentName && strings.details.componentName(errorComponentName);
  const errorTrace = (_ref = (_errorInfo$componentS = errorInfo === null || errorInfo === void 0 ? void 0 : errorInfo.componentStack) !== null && _errorInfo$componentS !== void 0 ? _errorInfo$componentS : error.stack) !== null && _ref !== void 0 ? _ref : error.toString();
  return ___EmotionJSX(EuiFlyout, {
    onClose: onClose,
    "aria-labelledby": simpleFlyoutTitleId,
    paddingSize: "none"
  }, ___EmotionJSX(EuiFlyoutHeader, {
    hasBorder: true
  }, ___EmotionJSX(EuiPanel, {
    paddingSize: "m",
    hasBorder: false,
    hasShadow: false
  }, ___EmotionJSX(EuiTitle, {
    size: "m"
  }, ___EmotionJSX("h2", null, strings.details.title())))), ___EmotionJSX(EuiFlyoutBody, null, ___EmotionJSX(EuiCodeBlock, {
    "data-test-subj": "errorBoundaryFatalDetailsErrorString"
  }, ___EmotionJSX("p", null, ((_error$stack = error.stack) !== null && _error$stack !== void 0 ? _error$stack : error.toString()) + '\n\n'), ___EmotionJSX("p", null, errorName, errorTrace))), ___EmotionJSX(EuiFlyoutFooter, null, ___EmotionJSX(EuiPanel, {
    paddingSize: "m",
    hasBorder: false,
    hasShadow: false
  }, ___EmotionJSX(EuiFlexGroup, {
    justifyContent: "spaceBetween"
  }, ___EmotionJSX(EuiFlexItem, {
    grow: false
  }, ___EmotionJSX(EuiButtonEmpty, {
    onClick: onClose,
    flush: "left"
  }, strings.details.closeButton())), ___EmotionJSX(EuiFlexItem, {
    grow: false
  }, ___EmotionJSX(EuiCopy, {
    textToCopy: errorName + '\n\n' + errorTrace
  }, copy => ___EmotionJSX(EuiButton, {
    onClick: copy,
    fill: true,
    iconType: "copyClipboard"
  }, strings.details.copyToClipboardButton())))))));
};
export const FatalPrompt = withErrorDetails(({
  showErrorDetails,
  onClickRefresh
}) => ___EmotionJSX(EuiEmptyPrompt, {
  title: ___EmotionJSX("h2", {
    "data-test-subj": "errorBoundaryFatalHeader"
  }, strings.page.callout.fatal.title()),
  color: "danger",
  iconType: "error",
  body: ___EmotionJSX(React.Fragment, null, ___EmotionJSX("p", {
    "data-test-subj": "errorBoundaryFatalPromptBody"
  }, strings.page.callout.fatal.body()), ___EmotionJSX("p", null, ___EmotionJSX(EuiButton, {
    color: "danger",
    iconType: "refresh",
    fill: true,
    onClick: onClickRefresh,
    "data-test-subj": "errorBoundaryFatalPromptReloadBtn"
  }, strings.page.callout.fatal.pageReloadButton())), ___EmotionJSX("p", null, ___EmotionJSX(EuiLink, {
    color: "danger",
    onClick: showErrorDetails,
    "data-test-subj": "errorBoundaryFatalShowDetailsBtn"
  }, strings.page.callout.fatal.showDetailsButton())))
}));
export const RecoverablePrompt = ({
  onClickRefresh
}) => {
  return ___EmotionJSX(EuiEmptyPrompt, {
    title: ___EmotionJSX("h2", {
      "data-test-subj": "errorBoundaryRecoverableHeader"
    }, strings.page.callout.recoverable.title()),
    color: "warning",
    iconType: "warning",
    body: ___EmotionJSX("p", {
      "data-test-subj": "errorBoundaryRecoverablePromptBody"
    }, strings.page.callout.recoverable.body()),
    actions: ___EmotionJSX(EuiButton, {
      color: "warning",
      iconType: "refresh",
      fill: true,
      onClick: onClickRefresh,
      "data-test-subj": "errorBoundaryRecoverablePromptReloadBtn"
    }, strings.page.callout.recoverable.pageReloadButton())
  });
};
export const SectionFatalPrompt = withErrorDetails(({
  sectionName,
  showErrorDetails
}) => {
  return ___EmotionJSX(EuiEmptyPrompt, {
    iconType: "error",
    color: "danger",
    title: ___EmotionJSX("h2", {
      "data-test-subj": "sectionErrorBoundaryPromptHeader"
    }, strings.section.callout.fatal.title(sectionName)),
    body: ___EmotionJSX(React.Fragment, null, ___EmotionJSX("p", {
      "data-test-subj": "sectionErrorBoundaryPromptBody"
    }, strings.section.callout.fatal.body(sectionName)), ___EmotionJSX("p", null, ___EmotionJSX(EuiLink, {
      color: "danger",
      onClick: showErrorDetails
    }, strings.section.callout.fatal.showDetailsButton())))
  });
});
export const SectionRecoverablePrompt = ({
  sectionName,
  onClickRefresh
}) => {
  return ___EmotionJSX(EuiEmptyPrompt, {
    color: "warning",
    iconType: "warning",
    title: ___EmotionJSX("h2", {
      "data-test-subj": "sectionErrorBoundaryPromptHeader"
    }, strings.section.callout.recoverable.title(sectionName)),
    body: ___EmotionJSX("p", {
      "data-test-subj": "sectionErrorBoundaryPromptBody"
    }, strings.section.callout.recoverable.body(sectionName)),
    actions: ___EmotionJSX(EuiButton, {
      color: "warning",
      iconType: "refresh",
      fill: true,
      onClick: onClickRefresh,
      "data-test-subj": "sectionErrorBoundaryRecoverBtn"
    }, strings.section.callout.recoverable.pageReloadButton())
  });
};
function withErrorDetails(PromptComponent) {
  return ({
    error,
    errorInfo,
    name,
    ...rest
  }) => {
    const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
    return ___EmotionJSX(React.Fragment, null, ___EmotionJSX(PromptComponent, _extends({}, rest, {
      showErrorDetails: () => setIsFlyoutVisible(true)
    })), isFlyoutVisible ? ___EmotionJSX(CodePanel, {
      error: error,
      errorInfo: errorInfo,
      name: name,
      onClose: () => setIsFlyoutVisible(false)
    }) : null);
  };
}